/** Etiketten drucken (Screen mieter/Etiketten): Format, Auswahl, Startfeld bei angefangenem Bogen, Vorschau, PDF. */
import { useTx } from "../i18n";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Button, EmptyState, Notice, Seitenzustand } from "../ui";
import { ShelfLabel } from "../ui/ShelfLabel";
import { formatEuro } from "@shared/geld";
import { etikettenOeffnen, useMeineArtikel } from "./artikel-api";

export function Etiketten() {
  const tx = useTx();
  const [params] = useSearchParams();
  const idsParam = useMemo(() => new Set((params.get("ids") ?? "").split(",").filter(Boolean)), [params]);
  const alle = useMeineArtikel(idsParam.size ? undefined : "im_regal", undefined, idsParam.size ? undefined : "druckliste");
  const [format, setFormat] = useState<"62x29" | "a4">("62x29");
  const [startfeld, setStartfeld] = useState(1);
  const [auswahl, setAuswahl] = useState<Set<string> | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const liste = (alle.data?.artikel ?? []).filter((a) => (idsParam.size ? idsParam.has(a.id) : true));
  const gewaehlt = auswahl ?? new Set(liste.map((a) => a.id));
  const erster = liste.find((a) => gewaehlt.has(a.id)) ?? liste[0];
  const toggle = (id: string) => setAuswahl(() => { const n = new Set(gewaehlt); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  async function drucken() {
    setFehler(null);
    setLaeuft(true);
    try {
      await etikettenOeffnen([...gewaehlt], format, startfeld);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : "Drucken hat nicht geklappt.");
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="sc-col" style={{ gap: "var(--space-4)" }}>
      <div className="sc-row">
        <Link to="/mieter/artikel" className="rg-btn rg-btn--quiet" aria-label="Zurück">‹</Link>
        <h1 className="title">{tx("Etiketten drucken")}</h1>
      </div>
      <div className="sc-toolbar" role="radiogroup" aria-label="Format">
        <button type="button" className="sc-pill" aria-pressed={format === "62x29"} onClick={() => setFormat("62x29")}>Etikettendrucker 62 × 29</button>
        <button type="button" className="sc-pill" aria-pressed={format === "a4"} onClick={() => setFormat("a4")}>A4-Bogen 4 × 11</button>
      </div>
      {fehler ? <Notice tone="error">{fehler}</Notice> : null}
      <Seitenzustand query={alle} lines={4} istLeer={() => liste.length === 0} leer={<EmptyState illustration="all-done" title={tx("Alle Etiketten gedruckt")} action={<Link to="/mieter/erfassen" className="rg-btn rg-btn--primary">Artikel erfassen</Link>}>Etiketten gibt es für Artikel im Regal.</EmptyState>}>
        {() => (
          <>
            <div className="sc-spread">
              <h2 className="heading">{idsParam.size ? `${liste.length} neue Artikel` : `${liste.length} ohne Etikett`}</h2>
              <Button variant="quiet" onClick={() => setAuswahl(gewaehlt.size === liste.length ? new Set() : new Set(liste.map((a) => a.id)))}>{gewaehlt.size === liste.length ? "Keine auswählen" : "Alle auswählen"}</Button>
            </div>
            <div className="sc-col" style={{ gap: 2 }}>
              {liste.map((a) => (
                <label key={a.id} className="sc-check" style={{ justifyContent: "space-between" }}>
                  <span className="sc-row"><input type="checkbox" checked={gewaehlt.has(a.id)} onChange={() => toggle(a.id)} /> {a.bezeichnung}</span>
                  <span className="sc-muted">{formatEuro(a.preisCent)}</span>
                </label>
              ))}
            </div>
            {format === "a4" ? (
              <div className="sc-col" style={{ gap: 4 }}>
                <h2 className="heading">{tx("Angefangener Bogen")}</h2>
                <p className="sc-muted sc-small">Start bei Feld {startfeld} – Zeile {Math.ceil(startfeld / 4)}, {["ganz links", "zweites von links", "drittes von links", "ganz rechts"][(startfeld - 1) % 4]}.</p>
                <span className="sc-row">
                  <Button variant="secondary" icon="minus" aria-label="Feld zurück" onClick={() => setStartfeld(Math.max(1, startfeld - 1))} />
                  <span className="sc-strong" style={{ minWidth: 32, textAlign: "center" }}>{startfeld}</span>
                  <Button variant="secondary" icon="plus" aria-label="Feld weiter" onClick={() => setStartfeld(Math.min(44, startfeld + 1))} />
                </span>
              </div>
            ) : null}
            {erster ? (
              <div className="sc-col" style={{ gap: 4 }}>
                <span className="sc-muted sc-small">Vorschau (vergrößert) · Schwarz auf Weiß, beim Druck 100 % wählen</span>
                <ShelfLabel code={erster.code} preisCent={erster.preisCent} titel={erster.bezeichnung} format={format} scale={4} />
              </div>
            ) : null}
            <div className="sc-phone__foot" style={{ position: "sticky", bottom: 72, margin: "0 calc(-1 * var(--space-4))", borderRadius: "var(--radius-lg) var(--radius-lg) 0 0" }}>
              <Button variant="primary" size="lg" block icon="printer" disabled={gewaehlt.size === 0} loading={laeuft} onClick={() => void drucken()}>PDF zum Drucken ({gewaehlt.size})</Button>
            </div>
          </>
        )}
      </Seitenzustand>
    </div>
  );
}
